import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { UIProvider } from '@xhr-labs/core-ui';
import App from './App';
import { ApiValidationError } from './http/api-client';
import { ErrorBoundary } from './components/ErrorBoundary';
import { adminTheme, adminThemeResolver } from './lib/theme';
import { initSentry } from './lib/monitoring/sentry';
import { initializeAnalytics } from './lib/analytics/analytics-client';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@xhr-labs/core-ui/dist/index.css';
import './index.css';

const FIVE_MINUTES_MS = 1000 * 60 * 5;
const MAX_RETRY_DELAY_MS = 30000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: FIVE_MINUTES_MS,
      retry: (failureCount, error) => {
        if (error instanceof ApiValidationError) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS),
      refetchOnWindowFocus: false,
    },
  },
});

initSentry();
initializeAnalytics();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find root element');

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <MantineProvider theme={adminTheme} cssVariablesResolver={adminThemeResolver}>
        <UIProvider>
          <Notifications />
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </UIProvider>
      </MantineProvider>
    </ErrorBoundary>
  </StrictMode>
);
